import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Badge } from '../components/ui/Badge';

interface AllocationChange {
  date: string;
  actor: string;
  before: Record<string, number>;
  after: Record<string, number>;
}

export interface Client {
  id: string; name: string; phone?: string; boss_name?: string; security_days?: number; active: boolean;
  contract_type?: string; contract_start?: string;
  deposit_amount?: number; deposit_received?: boolean;
  allocations?: Record<string, number>;
  allocations_history?: AllocationChange[];
  review_date?: string;
}

interface ItemTypeLite { code: string; label: string; }

interface ContractPreviewRow {
  line: number; client_text: string; client_id: string | null;
  contract_type: string; allocations: Record<string, number>; status: string; error: string;
}

export function Clients() {
  const { hasScope } = useAuth();
  const toast = useToast();
  const [list, setList] = useState<Client[] | null>(null);
  const [error, setError] = useState('');
  const [itemTypes, setItemTypes] = useState<ItemTypeLite[]>([]);

  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [boss, setBoss] = useState('');
  const [phone, setPhone] = useState('');
  const [secDays, setSecDays] = useState('2');
  const [contractType, setContractType] = useState('');
  const [contractStart, setContractStart] = useState('');
  const [depositAmount, setDepositAmount] = useState('0');
  const [depositReceived, setDepositReceived] = useState(false);
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [reviewDate, setReviewDate] = useState('');
  const [formMsg, setFormMsg] = useState<{ text: string; err: boolean } | null>(null);
  const [historyFor, setHistoryFor] = useState<Client | null>(null);

  const [contractsText, setContractsText] = useState('');
  const [contractsFileB64, setContractsFileB64] = useState<string | null>(null);
  const [contractsPreview, setContractsPreview] = useState<ContractPreviewRow[] | null>(null);
  const [contractsBusy, setContractsBusy] = useState(false);
  const [contractsMsg, setContractsMsg] = useState<{ text: string; err: boolean } | null>(null);

  async function load() {
    setError('');
    try { setList(await api<Client[]>('/v1/clients')); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Erreur de chargement.'); }
  }
  async function loadItemTypes() {
    try {
      const cfg = await api<{ item_types?: ItemTypeLite[] }>('/v1/config');
      setItemTypes(cfg.item_types || []);
    } catch { /* non bloquant : si la config n'est pas accessible, la dotation reste vide */ }
  }
  useEffect(() => { load(); loadItemTypes(); }, []);

  function edit(c: Client) {
    setId(c.id); setName(c.name); setBoss(c.boss_name || ''); setPhone(c.phone || '');
    setSecDays(String(c.security_days ?? 0));
    setContractType(c.contract_type || '');
    setContractStart(c.contract_start || '');
    setDepositAmount(String(c.deposit_amount ?? 0));
    setDepositReceived(!!c.deposit_received);
    const allocStrs: Record<string, string> = {};
    for (const [k, v] of Object.entries(c.allocations || {})) allocStrs[k] = String(v);
    setAllocations(allocStrs);
    setReviewDate(c.review_date || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function resetForm() {
    setId(''); setName(''); setBoss(''); setPhone(''); setSecDays('2');
    setContractType(''); setContractStart(''); setDepositAmount('0'); setDepositReceived(false);
    setAllocations({}); setReviewDate('');
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!id.trim() || !name.trim()) { setFormMsg({ text: 'Identifiant et nom sont obligatoires.', err: true }); return; }
    const allocationsOut: Record<string, number> = {};
    for (const [k, v] of Object.entries(allocations)) {
      const n = parseInt(v, 10);
      if (!isNaN(n) && n > 0) allocationsOut[k] = n;
    }
    try {
      await api(`/v1/clients/${encodeURIComponent(id.trim())}`, {
        method: 'PUT',
        body: {
          name: name.trim(), boss_name: boss.trim(), phone: phone.trim(), security_days: parseInt(secDays, 10) || 0,
          contract_type: contractType.trim(), contract_start: contractStart || null,
          deposit_amount: parseFloat(depositAmount) || 0, deposit_received: depositReceived,
          allocations: allocationsOut, review_date: reviewDate || null,
        },
      });
      setFormMsg({ text: 'Client enregistré.', err: false });
      toast('Client enregistré');
      load();
    } catch (e) {
      setFormMsg({ text: e instanceof ApiError ? e.message : 'Erreur.', err: true });
    }
  }

  async function onContractsFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) { setContractsFileB64(null); return; }
    const buf = await file.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    setContractsFileB64(btoa(binary));
  }

  async function runContractsImport(commit: boolean) {
    setContractsBusy(true); setContractsMsg(null);
    try {
      const body: Record<string, unknown> = { commit };
      if (contractsFileB64) body.file_b64 = contractsFileB64; else body.text = contractsText;
      const res = await api<{ preview?: ContractPreviewRow[]; applied?: string[]; skipped?: unknown[] }>(
        '/v1/clients/import', { method: 'POST', body });
      if (!commit) {
        setContractsPreview(res.preview || []);
      } else {
        setContractsMsg({ text: `Import appliqué : ${res.applied?.length ?? 0} client(s) mis à jour, ${res.skipped?.length ?? 0} ignoré(s).`, err: false });
        setContractsPreview(null);
        toast('Contrats importés');
        load();
      }
    } catch (e) { setContractsMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
    finally { setContractsBusy(false); }
  }

  return (
    <>
      <div className="pagehead"><h1>Clients</h1></div>

      {hasScope('admin') && (
        <div className="card">
          <h2>Nouveau client / modifier</h2>
          <form onSubmit={save}>
            <label htmlFor="cliId">Identifiant (CNUF)</label>
            <input id="cliId" value={id} onChange={(e) => setId(e.target.value)} placeholder="ex. client3" />
            <label htmlFor="cliName">Nom</label>
            <input id="cliName" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom affiché" />
            <div className="row2">
              <div><label htmlFor="cliBoss">Nom du patron</label><input id="cliBoss" value={boss} onChange={(e) => setBoss(e.target.value)} /></div>
              <div><label htmlFor="cliPhone">Téléphone</label><input id="cliPhone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+2126..." /></div>
            </div>
            <label htmlFor="cliSecDays">Jours de stock de sécurité</label>
            <input id="cliSecDays" type="number" min={0} value={secDays} onChange={(e) => setSecDays(e.target.value)} />

            <h3 style={{ marginTop: '1.2rem' }}>Contrat</h3>
            <div className="row2">
              <div>
                <label htmlFor="cliContractType">Type de contrat</label>
                <select id="cliContractType" value={contractType} onChange={(e) => setContractType(e.target.value)}>
                  <option value="">—</option>
                  <option value="mensuel">Mensuel</option>
                  <option value="annuel">Annuel</option>
                  <option value="ponctuel">Ponctuel</option>
                </select>
              </div>
              <div>
                <label htmlFor="cliContractStart">Date de début</label>
                <input id="cliContractStart" type="date" value={contractStart} onChange={(e) => setContractStart(e.target.value)} />
              </div>
            </div>

            <h3 style={{ marginTop: '1.2rem' }}>Caution</h3>
            <div className="row2">
              <div>
                <label htmlFor="cliDeposit">Montant (MAD)</label>
                <input id="cliDeposit" type="number" min={0} step="0.01" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.3rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
                  <input type="checkbox" checked={depositReceived} onChange={(e) => setDepositReceived(e.target.checked)} />
                  Caution perçue
                </label>
              </div>
            </div>

            <h3 style={{ marginTop: '1.2rem' }}>Dotation par type de caisse</h3>
            {itemTypes.length === 0 && <div className="muted">Aucun type de caisse configuré (voir Configuration).</div>}
            {itemTypes.map((it) => (
              <div key={it.code} className="row2" style={{ alignItems: 'center' }}>
                <label htmlFor={`alloc-${it.code}`} style={{ margin: 0 }}>{it.label} ({it.code})</label>
                <input id={`alloc-${it.code}`} type="number" min={0}
                  value={allocations[it.code] ?? ''}
                  onChange={(e) => setAllocations({ ...allocations, [it.code]: e.target.value })}
                  placeholder="0" />
              </div>
            ))}

            <h3 style={{ marginTop: '1.2rem' }}>Révision trimestrielle</h3>
            <label htmlFor="cliReview">Prochaine date de révision</label>
            <input id="cliReview" type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} />

            <div className="row" style={{ marginTop: '0.8rem', gap: '8px' }}>
              <button type="submit" className="primary">Enregistrer</button>
              <button type="button" className="small" onClick={resetForm}>Nouveau client</button>
            </div>
            {formMsg && <div className={formMsg.err ? 'err' : 'ok'}>{formMsg.text}</div>}
          </form>
        </div>
      )}

      {hasScope('admin') && (
        <div className="card">
          <h2>Import des contrats clients</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Colonnes attendues : CLIENT (ou CNUF), TYPE CONTRAT, DATE DEBUT, CAUTION MONTANT, CAUTION RECUE,
            DOTATION &lt;code&gt; (une colonne par type de caisse), DATE REVISION.
          </p>
          <label htmlFor="contractsFile">Fichier (.csv/.xlsx)</label>
          <input id="contractsFile" type="file" accept=".csv,.xlsx,text/csv" onChange={onContractsFile} />
          <label htmlFor="contractsText">...ou texte collé</label>
          <textarea id="contractsText" value={contractsText} onChange={(e) => setContractsText(e.target.value)}
            placeholder="Collez le tableau ici si vous n'avez pas de fichier" rows={4} />
          <div className="row" style={{ marginTop: '0.6rem' }}>
            <button type="button" className="small" onClick={() => runContractsImport(false)} disabled={contractsBusy}>Aperçu</button>
            {contractsPreview && (
              <button type="button" className="primary small" onClick={() => runContractsImport(true)} disabled={contractsBusy}>
                Valider l'import
              </button>
            )}
          </div>
          {contractsMsg && <div className={contractsMsg.err ? 'err' : 'ok'}>{contractsMsg.text}</div>}
          {contractsPreview && (
            <div className="tblwrap" style={{ marginTop: '0.6rem' }}>
              <table><tbody>
                <tr><th>Ligne</th><th>Client</th><th>Reconnu</th><th>Contrat</th><th>Dotation</th><th>Statut</th></tr>
                {contractsPreview.map((r, i) => (
                  <tr key={i}>
                    <td>{r.line}</td><td>{r.client_text}</td>
                    <td>{r.client_id ? <Badge kind="ok">oui</Badge> : <Badge kind="crit">non</Badge>}</td>
                    <td>{r.contract_type || '—'}</td>
                    <td>{Object.entries(r.allocations || {}).map(([k, v]) => `${k}:${v}`).join(', ') || '—'}</td>
                    <td>{r.status === 'ok' ? <Badge kind="ok">ok</Badge> : <Badge kind="crit">{r.error}</Badge>}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Liste des clients</h2>
          <button className="small" onClick={load}>Actualiser</button>
        </div>
        {error && <div className="err">{error}</div>}
        {!list && !error && <div className="muted">Chargement...</div>}
        {list && list.length === 0 && <span className="muted">Aucun client.</span>}
        {list && list.length > 0 && (
          <div className="tblwrap">
            <table>
              <tbody>
                <tr><th>Nom</th><th>Téléphone</th><th>Contrat</th><th>Caution</th><th>Révision</th><th>Statut</th><th></th></tr>
                {list.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.phone || '—'}</td>
                    <td>{c.contract_type || '—'}</td>
                    <td>{c.deposit_amount ? `${c.deposit_amount} MAD${c.deposit_received ? ' ✓' : ' (en attente)'}` : '—'}</td>
                    <td>{c.review_date || '—'}</td>
                    <td>{c.active ? <Badge kind="ok">actif</Badge> : <Badge kind="crit">inactif</Badge>}</td>
                    <td style={{ display: 'flex', gap: '6px' }}>
                      <button className="small" onClick={() => edit(c)}>Modifier</button>
                      {!!(c.allocations_history && c.allocations_history.length) && (
                        <button className="small" onClick={() => setHistoryFor(c)}>Historique</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {historyFor && (
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>Historique des dotations — {historyFor.name}</h2>
            <button className="small" onClick={() => setHistoryFor(null)}>Fermer</button>
          </div>
          <div className="tblwrap">
            <table>
              <tbody>
                <tr><th>Date</th><th>Par</th><th>Avant</th><th>Après</th></tr>
                {[...(historyFor.allocations_history || [])].reverse().map((h, i) => (
                  <tr key={i}>
                    <td>{h.date}</td>
                    <td>{h.actor}</td>
                    <td>{Object.entries(h.before || {}).map(([k, v]) => `${k}:${v}`).join(', ') || '—'}</td>
                    <td>{Object.entries(h.after || {}).map(([k, v]) => `${k}:${v}`).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
